/**
 * Per-browser persistence layer for the episode detail page's view config.
 *
 * The URL is the source of truth (so links / saved views capture state).
 * This store provides the *fallback* when no URL params are present —
 * e.g. the user navigates from the list page to an episode for the first
 * time and we want their last-chosen camera mode / speed / hidden lanes
 * to apply.
 *
 * Saved views (Supabase-backed) override the store on apply by writing
 * to the URL, which re-hydrates the store via the wiring hook in
 * `@bastion/episodes`.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

const STORAGE_KEY = "bastion.episode-detail.view";

/**
 * Camera selection entry: a camera name (video basename without extension,
 * e.g. "left_camera_rgb") or one of the legacy bare position tokens
 * "left" / "center" / "right". Legacy tokens remain valid forever — they
 * live in persisted store state and shared URLs — and are resolved against
 * the episode's actual camera list at consumption time
 * (`resolveVisibleCameraFiles` in @bastion/data).
 */
export type EpisodeDetailCameraId = string;
/** Deprecated alias for EpisodeDetailCameraId; selection is name-based now. */
export type EpisodeDetailCameraPosition = string;
export const EPISODE_CAMERA_LAYOUT_PRESETS = ["fit", "head", "three", "equal"] as const;
export type EpisodeCameraLayoutPreset = (typeof EPISODE_CAMERA_LAYOUT_PRESETS)[number];
export type EpisodeCameraWristMode = "shown" | "minimized" | "hidden";
export interface EpisodeCameraWristLayout {
  /** Position within the selected coordinate space's available travel, from zero to one. */
  x?: number;
  y?: number;
  /** Preferred fraction of the primary image width. Missing uses 0.24. */
  size?: number;
  /** Manual moves may use the black viewer area. Missing stays head-relative. */
  space?: "viewer";
  mode: EpisodeCameraWristMode;
}
export interface EpisodeCameraLayout {
  preset: EpisodeCameraLayoutPreset;
  primaryCamera?: string;
  wrists?: Record<string, EpisodeCameraWristLayout>;
  wristSize?: number;
}
export const DEFAULT_EPISODE_CAMERA_LAYOUT: EpisodeCameraLayout = { preset: "fit" };
export interface EpisodeViewerSizing {
  mode: "fixed" | "window";
  /** Preferred fixed video height in pixels. */
  height?: number;
  /** Preferred lower-pane height in pixels. */
  lowerHeight?: number;
}
export type EpisodeDetailSpeed = 1 | 2 | 4 | 8 | 16 | 32;
export type EpisodeDetailsPlacement = "sidebar" | "page";
export const EPISODE_DETAILS_SECTION_KEYS = [
  "overview", "task", "review", "tags", "hardware", "operations", "episode_metadata",
] as const;
export type EpisodeDetailsSectionKey = (typeof EPISODE_DETAILS_SECTION_KEYS)[number];
export type EpisodeDetailsSectionPlacements = Partial<Record<EpisodeDetailsSectionKey, EpisodeDetailsPlacement>>;
export const EPISODE_DETAILS_DISCLOSURE_KEYS = ["hardware", "operations", "episode_metadata", "recording"] as const;
export const EPISODE_DETAIL_RIGHT_PANEL_TABS = ["details", "joints", "review", "comments"] as const;
export type EpisodeDetailRightPanelTab = (typeof EPISODE_DETAIL_RIGHT_PANEL_TABS)[number];
export interface EpisodeDetailRightPanel {
  open: boolean;
  tab: EpisodeDetailRightPanelTab;
  wide: boolean;
  width?: number | null;
}

/**
 * Video frame size:
 *  - `"standard"` — fits inside the page content area (default; matches
 *    the swim-lane stack's max width).
 *  - `"wide"` — breaks out of the content padding and fills the viewport
 *    width, useful for spreading the timeline + video across the screen.
 *
 * Fullscreen is a one-shot browser action (HTMLElement.requestFullscreen)
 * and intentionally NOT persisted here.
 */
export type EpisodeDetailVideoSize = "standard" | "theater";

/**
 * Layout variant for the SOP Predicates swim lane. Mirrors
 * `ViolationsLaneVariant` from `@bastion/video` but is declared here
 * (the canonical home of the view-config snapshot) as a string-literal
 * union to avoid a cross-package type dependency from stores into
 * video. Keep the two unions in lockstep.
 *
 *  - `"matrix"` (UI label: "Compact") — tight column-per-subtask matrix
 *    aligned to timeline offsets.
 *  - `"roomy"`  — equal-share cards spanning the full lane width, with
 *    saturated subtask-hue headers.
 *  - `"rows"`   — one row per predicate, with collapsible subtask groups
 *    and a per-group Pass-all action.
 */
export type SopPredicatesVariant = "matrix" | "roomy" | "rows";

export interface EpisodeDetailViewState {
  visibleCameras: EpisodeDetailCameraId[];
  cameraLayout?: EpisodeCameraLayout;
  viewerSizing?: EpisodeViewerSizing;
  defaultSpeed: EpisodeDetailSpeed;
  videoSize: EpisodeDetailVideoSize;
  detailsPlacement?: EpisodeDetailsPlacement;
  detailsSectionPlacements?: EpisodeDetailsSectionPlacements;
  lanesOpen?: boolean;
  expandedDetailsSections?: string[];
  rightPanel?: EpisodeDetailRightPanel;
  hiddenLanes: string[];
  hiddenMetaColumns: string[];
  hiddenMetaFields: string[];
  collapsedGroups: string[];
  groupOrder: string[];
  laneOrder: Record<string, string[]>;
  sopPredicatesVariant: SopPredicatesVariant;
  patch: (next: Partial<EpisodeDetailViewSnapshot>) => void;
  reset: () => void;
}

/** The serializable subset (what we persist + what the URL codec round-trips). */
export interface EpisodeDetailViewSnapshot {
  visibleCameras: EpisodeDetailCameraId[];
  /** Camera arrangement and floating wrist state use stable camera names. */
  cameraLayout?: EpisodeCameraLayout;
  /** Missing retains the legacy viewer and page geometry. */
  viewerSizing?: EpisodeViewerSizing;
  defaultSpeed: EpisodeDetailSpeed;
  videoSize: EpisodeDetailVideoSize;
  /** Missing in older views. Those views keep details in the sidebar. */
  detailsPlacement?: EpisodeDetailsPlacement;
  /** Section choices override the bulk location. Older views have no overrides. */
  detailsSectionPlacements?: EpisodeDetailsSectionPlacements;
  /** Missing in older views means the timed lanes are open. */
  lanesOpen?: boolean;
  /** Native details disclosures that are expanded. */
  expandedDetailsSections?: string[];
  /** Missing preserves the current personal panel until the host captures it. */
  rightPanel?: EpisodeDetailRightPanel;
  hiddenLanes: string[];
  hiddenMetaColumns: string[];
  hiddenMetaFields: string[];
  collapsedGroups: string[];
  groupOrder: string[];
  laneOrder: Record<string, string[]>;
  sopPredicatesVariant: SopPredicatesVariant;
}

export const DEFAULT_EPISODE_DETAIL_VIEW: EpisodeDetailViewSnapshot = {
  visibleCameras: ["center"],
  cameraLayout: DEFAULT_EPISODE_CAMERA_LAYOUT,
  viewerSizing: undefined,
  defaultSpeed: 8,
  videoSize: "standard",
  detailsPlacement: "sidebar",
  detailsSectionPlacements: {},
  lanesOpen: true,
  expandedDetailsSections: [],
  rightPanel: undefined,
  // Lanes we hide on a fresh load:
  //   - Trajectory lanes are heavy (one server fetch per episode).
  //   - Relative-speed siblings duplicate the duration-bucket lane and
  //     are most useful when the user explicitly asks how this run
  //     compares to peers; keep them an opt-in to keep the rail calm.
  hiddenLanes: [
    "left_arm_trajectory",
    "right_arm_trajectory",
    "left_gripper",
    "right_gripper",
    "task_relative_avg",
    "task_relative_min",
    "subtask_relative_avg",
    "subtask_relative_min",
  ],
  hiddenMetaColumns: [],
  hiddenMetaFields: [],
  collapsedGroups: [],
  groupOrder: [],
  laneOrder: {},
  sopPredicatesVariant: "roomy",
};

export const useEpisodeDetailViewStore = create<EpisodeDetailViewState>()(
  persist(
    (set) => ({
      ...DEFAULT_EPISODE_DETAIL_VIEW,
      patch: (next) => set((s) => ({ ...s, ...next })),
      reset: () => set(DEFAULT_EPISODE_DETAIL_VIEW),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        visibleCameras: state.visibleCameras,
        cameraLayout: state.cameraLayout,
        viewerSizing: state.viewerSizing,
        defaultSpeed: state.defaultSpeed,
        videoSize: state.videoSize,
        detailsPlacement: state.detailsPlacement,
        detailsSectionPlacements: state.detailsSectionPlacements,
        lanesOpen: state.lanesOpen,
        expandedDetailsSections: state.expandedDetailsSections,
        rightPanel: state.rightPanel,
        hiddenLanes: state.hiddenLanes,
        hiddenMetaColumns: state.hiddenMetaColumns,
        hiddenMetaFields: state.hiddenMetaFields,
        collapsedGroups: state.collapsedGroups,
        groupOrder: state.groupOrder,
        laneOrder: state.laneOrder,
        sopPredicatesVariant: state.sopPredicatesVariant,
      }),
    },
  ),
);

type DetailsLayout = Pick<EpisodeDetailViewSnapshot, "detailsPlacement" | "detailsSectionPlacements" | "hiddenMetaColumns">;

export function effectiveEpisodeDetailsPlacement(snapshot: DetailsLayout, section: EpisodeDetailsSectionKey): EpisodeDetailsPlacement {
  const override = snapshot.detailsSectionPlacements?.[section];
  if (override === "page" || override === "sidebar") return override;
  return snapshot.detailsPlacement === "page" ? "page" : "sidebar";
}

function hasDetailsAt(snapshot: DetailsLayout, placement: EpisodeDetailsPlacement): boolean {
  return EPISODE_DETAILS_SECTION_KEYS.some((section) =>
    !snapshot.hiddenMetaColumns.includes(section) && effectiveEpisodeDetailsPlacement(snapshot, section) === placement,
  );
}

export function hasSidebarEpisodeDetails(snapshot: DetailsLayout): boolean {
  return hasDetailsAt(snapshot, "sidebar");
}

export function hasPageEpisodeDetails(snapshot: DetailsLayout): boolean {
  return hasDetailsAt(snapshot, "page");
}
